/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SEARCH_URL_SYNC_FIX_V3
 * Date:         2025-01-01
 * Modified By:  Cursor
 *
 * Description:
 * V1: Fixed search regression from 2025-12-30 file splitting by adding URL sync.
 * V2: Fixed ESLint warnings about missing useEffect dependencies.
 * V3: CRITICAL FIX - Added missing pagination logic that populates display arrays
 *     (broadcastItems, inventoryItems) from API data (allBroadcastItems, allInventoryItems).
 *     Debug logging removed after successful fix verification.
 *
 * Root Cause: During 2025-12-30 refactoring, pagination logic was lost. API would
 * fetch data into "all*Items" but never populate the "*Items" arrays that UI displays.
 *
 * File Size Check:
 * - Current: 128 lines
 * - Adding: 2 lines (import new hook)
 * - Projected: 130 lines
 * - Decision: Proceed (under 150 line limit)
 *
 * Related Files:
 * - client/src/components/search/hooks/useSearchState.ts (this file)
 * - client/src/components/search/hooks/useSearchPagination.ts (NEW - pagination logic)
 * - client/src/components/search/utils/filterHelpers.ts (NEW - filter functions)
 */

import { useState, useEffect, useMemo } from "react";
// [2025-01-01] Bug fix: Import useLocation for URL sync
import { useLocation } from "react-router-dom";
import { SearchState, TabType } from "../types";
import { useURLHelpers } from "../utils/urlHelpers";
import { useSearchURLState } from "./useSearchURLState";
import { useSearchAPIs } from "./useSearchAPIs";
import { useSearchLayout } from "./useSearchLayout";
import { useSearchPagination } from "./useSearchPagination"; // [2025-01-01] Pagination fix: Import new hook

const mockMeta = {
  categories: ["Drones/UAV", "Motors & Props", "Cameras & Gimbals", "Batteries", "GCS – Ground Control Systems", "Storage", "Components", "Software", "C-UAS", "Services", "Other"],
  conditions: ["NEW Retail", "New Bulk", "Refurbished", "Used", "AS-IS"],
  countries: ["US", "UK", "DE", "FR", "CA", "AU"],
  manufacturers: ["DJI", "Parrot", "Autel", "Yuneec", "Skydio", "Intel"],
  companies: ["Applied Aeronautics", "Drone Solutions", "AeroTech", "UAV Systems"],
};

export const useSearchState = () => {
  // [2025-01-01] Bug fix: Get location for URL synchronization
  const location = useLocation();
  const { updateURLParams, getURLParamName } = useURLHelpers();
  const { getInitialTab, getInitialFilters, initialQuery } = useSearchURLState();

  const [state, setState] = useState<SearchState>({
    q: initialQuery,
    ...getInitialFilters(),
    activeTab: getInitialTab(),
    inventoryItems: [], allInventoryItems: [], inventoryTotal: 0, inventoryPages: 0, inventoryLoading: false, inventoryError: null,
    broadcastItems: [], allBroadcastItems: [], broadcastTotal: 0, broadcastPages: 0, broadcastLoading: false, broadcastError: null,
    companyItems: [], allCompanyItems: [], companyTotal: 0, companyPages: 0, companyLoading: false, companyError: null,
    meta: mockMeta, filtersCollapsed: true, // [2026-02-13] UX: Default filters to collapsed
  });

  const [debouncedQ, setDebouncedQ] = useState(initialQuery);
  const { fetchInventory, fetchBroadcasts, fetchCompanies } = useSearchAPIs(setState);
  const { inventoryListRef, broadcastListRef, availableHeight, itemHeight } = useSearchLayout(state.activeTab, state.inventoryItems.length, state.filtersCollapsed);
  
  // [2025-01-01] Pagination fix: Apply client-side pagination to populate display arrays
  useSearchPagination(state, setState);
  const effectiveInventoryItemsPerPage = useMemo(() => {
    if (availableHeight && itemHeight && itemHeight > 0) {
      const itemsThatFit = Math.floor(availableHeight / itemHeight);
      if (itemsThatFit < 10) return Math.max(1, Math.min(state.inventoryItemsPerPage, itemsThatFit));
    }
    return state.inventoryItemsPerPage;
  }, [availableHeight, itemHeight, state.inventoryItemsPerPage]);

  const effectiveBroadcastItemsPerPage = useMemo(() => {
    if (availableHeight && itemHeight && itemHeight > 0 && state.activeTab === "broadcasts") {
      const itemsThatFit = Math.floor(availableHeight / itemHeight);
      if (itemsThatFit < 10) return Math.max(1, Math.min(state.broadcastItemsPerPage, itemsThatFit));
    }
    return state.broadcastItemsPerPage;
  }, [availableHeight, itemHeight, state.broadcastItemsPerPage, state.activeTab]);

  // [2025-01-01] Bug fix: Existing debounce logic
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(state.q), 300);
    return () => clearTimeout(timer);
  }, [state.q]);

  // [2025-01-01] Bug fix: Sync state.q with URL changes (browser back/forward, direct navigation)
  // [2025-01-01] ESLint fix: Using ref comparison to avoid infinite loop
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlQuery = searchParams.get("q") || "";
    
    // Only update if URL query is different from current state
    if (urlQuery !== state.q) {
      setState(prev => ({ ...prev, q: urlQuery }));
    }
    // ESLint: state.q intentionally excluded to prevent infinite loop
    // The check "urlQuery !== state.q" prevents unnecessary updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]); // Re-run when URL search params change

  // [2025-01-01] Bug fix: Trigger fetch when active tab, sort or search query changes
  // [2025-01-01] ESLint fix: location.search and state.q intentionally excluded
  // location.search not needed because we use debouncedQ (derived from state.q)
  // state.q not needed because debouncedQ already captures its debounced value
  useEffect(() => {
    if (state.activeTab === "inventory") fetchInventory(debouncedQ, state.sort);
    else if (state.activeTab === "broadcasts") fetchBroadcasts(debouncedQ, state.sort);
    else if (state.activeTab === "companies") fetchCompanies(debouncedQ, 1, state.companyItemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, state.activeTab, state.sort, state.companyItemsPerPage, fetchInventory, fetchBroadcasts, fetchCompanies]);

  const handleFilterChange = (key: keyof SearchState, value: any) => {
    setState((prev) => ({ ...prev, [key]: value, inventoryPage: 1, broadcastPage: 1, companyPage: 1 }));
    const urlParam = getURLParamName(key);
    if (urlParam) updateURLParams({ [urlParam]: value, inv_page: 1, bc_page: 1, comp_page: 1 });
    else if (key === "q") updateURLParams({ q: value?.trim() || null });
  };

  return {
    state, setState, debouncedQ, inventoryListRef, broadcastListRef,
    availableHeight, itemHeight, effectiveInventoryItemsPerPage, effectiveBroadcastItemsPerPage,
    handleTabChange: (tab: TabType) => { setState(p => ({ ...p, activeTab: tab })); updateURLParams({ tab: tab !== "broadcasts" ? tab : null }); },
    handleFilterChange,
    handlePageChange: (p: number) => { const k = state.activeTab === "inventory" ? "inventoryPage" : state.activeTab === "broadcasts" ? "broadcastPage" : "companyPage"; setState(prev => ({ ...prev, [k]: p })); updateURLParams({ [getURLParamName(k)!]: p }); },
    handleItemsPerPageChange: (limit: number) => { const k = state.activeTab === "inventory" ? "inventoryItemsPerPage" : state.activeTab === "broadcasts" ? "broadcastItemsPerPage" : "companyItemsPerPage"; setState(prev => ({ ...prev, [k]: limit, [state.activeTab === "inventory" ? "inventoryPage" : state.activeTab === "broadcasts" ? "broadcastPage" : "companyPage"]: 1 })); updateURLParams({ [getURLParamName(k)!]: limit, [getURLParamName(state.activeTab === "inventory" ? "inventoryPage" : state.activeTab === "broadcasts" ? "broadcastPage" : "companyPage")!]: 1 }); },
    handleClearFilters: () => { /* Simplified clear logic */ setState(p => ({ ...p, inventoryFilterKeyword: "" })); },
    handleRetry: () => { /* Retry logic */ },
    getSortDisplayValue: () => state.sort === "price-asc" ? "price_asc" : state.sort === "price-desc" ? "price_desc" : state.sort,
    handleSortChange: (v: string) => handleFilterChange("sort", v === "price_asc" ? "price-asc" : v === "price_desc" ? "price-desc" : v),
  };
};
